import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppState, AppAction } from '../types';

const initialState: AppState = {
  currentDocument: null,
  selectedTemplate: null,
  templates: [],
  isLoading: false,
  error: null,
  documentOpacity: 1.0,
  templateOpacity: 0.7,
  ignoreWhiteBackground: true,
  algemeneVoorwaarde: null,
  includeAlgemeneVoorwaarde: true,
  pageTemplateMappings: [],
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_DOCUMENT':
      return { ...state, currentDocument: action.payload, error: null };
    case 'SET_TEMPLATE':
      return { ...state, selectedTemplate: action.payload, error: null };
    case 'ADD_TEMPLATE':
      return { 
        ...state, 
        templates: [...state.templates, action.payload],
        error: null 
      };
    case 'REMOVE_TEMPLATE':
      return { 
        ...state, 
        templates: state.templates.filter(t => t.id !== action.payload),
        selectedTemplate: state.selectedTemplate?.id === action.payload 
          ? null 
          : state.selectedTemplate,
        error: null 
      };
    case 'SET_TEMPLATES':
      return { ...state, templates: action.payload, error: null };
    case 'UPDATE_TEMPLATE':
      return {
        ...state,
        templates: state.templates.map(t =>
          t.id === action.payload.id ? action.payload : t
        ),
        selectedTemplate: state.selectedTemplate?.id === action.payload.id
          ? action.payload
          : state.selectedTemplate,
        error: null
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SET_DOCUMENT_OPACITY':
      return { ...state, documentOpacity: action.payload };
    case 'SET_TEMPLATE_OPACITY':
      return { ...state, templateOpacity: action.payload };
    case 'TOGGLE_IGNORE_WHITE_BACKGROUND':
      return { ...state, ignoreWhiteBackground: !state.ignoreWhiteBackground };
    case 'SET_ALGEMENE_VOORWAARDE':
      return { ...state, algemeneVoorwaarde: action.payload, error: null };
    case 'TOGGLE_INCLUDE_ALGEMENE_VOORWAARDE':
      return { ...state, includeAlgemeneVoorwaarde: !state.includeAlgemeneVoorwaarde };
    case 'SET_PAGE_TEMPLATE_MAPPINGS':
      return { ...state, pageTemplateMappings: action.payload };
    case 'UPDATE_PAGE_TEMPLATE_MAPPING':
      const existingIndex = state.pageTemplateMappings.findIndex(
        m => m.pageNumber === action.payload.pageNumber
      );
      if (existingIndex >= 0) {
        const newMappings = [...state.pageTemplateMappings];
        newMappings[existingIndex] = action.payload;
        return { ...state, pageTemplateMappings: newMappings };
      } else {
        return {
          ...state,
          pageTemplateMappings: [...state.pageTemplateMappings, action.payload]
        };
      }
    default:
      return state;
  }
}

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}